import React from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as CalendarIcon } from '@assets/svg/manage-equipment/ic-calendar.svg';
import { ReactComponent as DeleteIcon } from '@assets/svg/manage-equipment/ic-delete.svg';
import { ReactComponent as DownloadIcon } from '@assets/svg/manage-equipment/ic-download.svg';
import { ReactComponent as EditIcon } from '@assets/svg/manage-equipment/ic-edit.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useModalActions } from '@store/useModalStore';

import type { IEquipmentItem } from '../../model/equipmentsList';
import { useEquipmentsActivateMutation } from '../../services/useEquipmentsActivateMutation';
import {
  selectManageEquipmentInactiveEquipment,
  selectManageEquipmentSelectedAllValue,
  selectManageEquipmentSelectedRows,
  useManageEquipmentActions,
} from '../../store/useManageEquipmentStore';
import style from './style.module.scss';

interface IProps {
  id: string;
  isManageEquipmentDeactivation: boolean;
}

const ContextMenu: React.FC<IProps> = ({ id, isManageEquipmentDeactivation }) => {
  const { t } = useTranslation();
  const { setActiveModal, setSecondaryModal } = useModalActions();
  const selectedAll = selectManageEquipmentSelectedAllValue();
  const selectedRows = selectManageEquipmentSelectedRows();
  const inactiveEquipment: Array<IEquipmentItem> = selectManageEquipmentInactiveEquipment();
  const { setEquipmentSelectedRows, setEquipmentSelectedAll, setInactiveEquipment } =
    useManageEquipmentActions();

  const { mutate: equipmentsActivateMutate } = useEquipmentsActivateMutation();

  const handleActivateEquipment = (row: IEquipmentItem) => {
    equipmentsActivateMutate(
      { equipments: [row.id] },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Your changes have been saved.') });

          if (selectedAll) {
            setEquipmentSelectedAll(false);
          }
          const rows = { ...selectedRows };
          delete rows[row.id];
          setEquipmentSelectedRows(rows);
          setInactiveEquipment(inactiveEquipment.filter((eq) => eq.id !== row.id));
        },
      },
    );
  };

  const onDeactivateEquipment = (row: IEquipmentItem) => {
    const { deactivateTable } = row;
    if (deactivateTable) {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: `${t('Activate Equipment')} ${row.name}`,
          bodyContent: t('Are you sure you want to activate the selected equipment?'),
          onConfirm: () => handleActivateEquipment(row),
        },
      });
    } else {
      setSecondaryModal({
        name: MODAL_NAMES.modalConfirm,
        options: {
          bodyTitle: `${t('Deactivate Equipment')} ${row.name}`,
          bodyContent: t('Are you sure you want to deactivate the selected equipment?'),
          onConfirm: () => {
            setActiveModal({
              name: MODAL_NAMES.equipmentDeactivateModal,
              options: { selectedEquipments: [row] },
            });
          },
        },
      });
    }
  };

  const onEditDeactivationPeriodEquipment = (row: IEquipmentItem) => {
    setActiveModal({
      name: MODAL_NAMES.editEquipmentDeactivationPeriodModal,
      options: {
        selectedEquipments: [row],
        deactivateTable: row.deactivateTable,
        isDeactivated: !!row.is_deactivated,
      },
    });
  };

  return (
    <Tooltip
      id={id}
      place="left"
      openOnClick
      className={style.contextMenuPopover}
      clickable
      disableStyleInjection
      noArrow
      render={({ content }) => {
        if (!content) {
          return null;
        }
        const row: IEquipmentItem = JSON.parse(content);

        return (
          <>
            <div
              className={style.contextMenuPopoverItem}
              onClick={() =>
                setActiveModal({ name: MODAL_NAMES.equipmentEditModal, options: { equipmentData: row } })
              }
            >
              <EditIcon className={style.menuIcon} />
              {t('Edit')}
            </div>
            {isManageEquipmentDeactivation && (
              <>
                <div className={style.contextMenuPopoverItem} onClick={() => onDeactivateEquipment(row)}>
                  <DeleteIcon className={style.menuIcon} />
                  {t(row.deactivateTable ? 'Activate' : 'Deactivate')}
                </div>
                {row.deactivateTable && (
                  <div
                    className={style.contextMenuPopoverItem}
                    onClick={() => onEditDeactivationPeriodEquipment(row)}
                  >
                    <CalendarIcon className={style.calendarIcon} />
                    {t('Edit Deactivation Period')}
                  </div>
                )}
              </>
            )}

            {!!row.oilReport?.length && (
              <div
                className={style.contextMenuPopoverItem}
                onClick={() =>
                  setActiveModal({
                    name: MODAL_NAMES.oilReportModal,
                    options: { equipmentName: row.name, reports: row.oilReport },
                  })
                }
              >
                <DownloadIcon className={style.menuIcon} />
                {t('Download Oil Analysis Report')}
              </div>
            )}
          </>
        );
      }}
    />
  );
};

export default ContextMenu;
